import React, { useEffect, useState, useImperativeHandle } from 'react'
import { connect } from 'react-redux'
import { FormattedMessage } from 'react-intl';
import { 
  useTable,
  useSortBy,
  useFlexLayout,
  // useBlockLayout,
  useResizeColumns,
  useRowSelect,
  useExpanded
} from 'react-table'
import Toast from '../Toast/Toast';
import ColumnToggleButton from '../Buttons/ColumnToggleButton';
import Searchbar from '../Searchbar/Searchbar';
import SubTable from './SubTable';
import DragIcon from '../../assets/icons/table/drag.svg';
import ArrowDownIcon from '../../assets/icons/table/arrow-down.svg';
import CellWithTooltip from './CellWithTooltip';
import tableModal from '../../utils/tableModal';
import { rowFilter } from '../../utils/tableFilter';
import commonFunctions from '../../utils/commonFunctions';
import { setUserState } from '../../store/user';
import './Table.css'
import '../Modals/Modals.css'
import './Toolbar.css'

function Table(props) {
	const {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey = null,
		pageType,
    // modalId = null,
		columns, 
		data, setData,
    dataMap,
    dataLoaded,
    // Data Handling props
    generateDataSlice,
    setDataCount,
    sortMap,
    // Table props
		success, setSuccess,
		error, setError,
		setSortBy,
		sortByCol,
		sortByAsc,
    dataCount,
		totalDataCount,
		selectedIds,
		startIndex = null,
    // Select Row Props
    setSelectedIds,
		// Page function props
    handleEditClick = null,
    handleSingleDelete = null,
    subTableProps = null,
    tableRef,
    tableWrapperRef,
    searchValue, 
    setSearchValue,
    singleSelect = false, // Checkbox props
    hasRowsWithChildren = null, // If the table has rows with children
    buttons: {
      titleButtons = null, 
      toolbarLeftButtons = null, 
      toolbarRightButtons = null, 
      footerButtons = null,
    } = {},
    user,
    selectionToolbar = null,
  } = props;

  const ROW_HEIGHT = 70;

  const [containerHeight, setContainerHeight] = useState('calc(100vh - 300px)');
  const [lastSearchValue, setLastSearchValue] = useState('');
  const [dataStatus, setDataStatus] = useState('');
	const [lastClickedRow, setLastClickedRow] = useState(null);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const filter = (column) => {
      let flag  = column.isVisible === undefined || column.isVisible === true;
      if (user && user.preferences && 
          user.preferences.hiddenColumns && 
          user.preferences.hiddenColumns[tableKey]) {
        flag = flag && !user.preferences.hiddenColumns[tableKey].includes(column.id);
      }
      return flag;
    };
    return columns.filter(filter).map((column) => column.id);
  });

  const [columnsNew] = useState(() => {
    return columns.map((column) => {
      // Set the column width to the saved value, or a default width of 150 if no saved value exists
      column.width = tableModal.loadColumnWidth(pageType, column.id, column.width ? column.width : undefined);
      return column;
    });
  });

  const [expandeds, setExpandeds] = useState('');

  // Search on interval
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (searchValue !== lastSearchValue){
        setLastSearchValue(searchValue);
        search();
      }
    }, 500);
    return () => {
      window.clearInterval(timer);
    };
  });

  useImperativeHandle(tableRef, () => ({ // 暴露给父组件的方法
    search,
  }))

  /**
   * Filters data based on searchValue and updates the data state
   * @returns 
   */
  const search = (dataMapTem = dataMap, startIndex = 0) => {
    console.log("doing a search")
    const text = searchValue;
  
    // Extract search accessors from columns
    const searchAccessors = columns
      .filter(col => col.isVisible && col.accessor)
      .map(col => col.accessor);
  
    // Keep track of parents that need to be expanded
    const parentsToExpand = new Set();
    
    // First pass: find all matching items and their parent chain
    const matchingItems = new Set();
    Object.keys(dataMapTem.data).forEach((id) => {
      const item = dataMapTem.data[id];
      if (rowFilter(item, text, searchAccessors)) {
        matchingItems.add(id);
        
        // Add this item's parent chain to the expansion set
        let currentItem = item;
        while (currentItem.parentId) {
          parentsToExpand.add(currentItem.parentId);
          currentItem = dataMapTem.data[currentItem.parentId];
          if (!currentItem) break;
        }
      }
    });

    // Expand all parent items that contain matching children
    parentsToExpand.forEach(parentId => {
      if (dataMapTem.data[parentId]) {
        dataMapTem.data[parentId].isExpanded = true;
      }
    });

    // Get all visible IDs (root items and children of expanded items)
    const getVisibleIds = () => {
      const visibleIds = [];
      const processNode = (id) => {
        visibleIds.push(id);
        const item = dataMapTem.data[id];
        if (item.isExpanded && item.children) {
          item.children.forEach(child => processNode(child.id));
        }
      };

      // Start with root level items
      dataMapTem.ids.forEach(id => processNode(id));
      return visibleIds;
    };

    // Filter visible IDs to those that either match the search or are in the parent chain
    const filteredIds = getVisibleIds().filter(id => 
      matchingItems.has(id) || parentsToExpand.has(id)
    );
  
    const sortedIds = sortMap(dataMapTem, filteredIds);
    setDataCount(sortedIds.length);
    setData(generateDataSlice(dataMapTem, startIndex));
  };
  
  /**
   * Toggles the visibility of a column
   * @param {*} columnId 
   */
  const toggleColumn = (columnId) => {
    const cols = visibleColumns.includes(columnId) ? visibleColumns.filter((id) => id !== columnId): [...visibleColumns, columnId];
    setVisibleColumns(cols);

    // Process and save the user's hide column preferences
    commonFunctions.processHideColumnPreferences(tableKey, user, columns, cols, props, setUserState);
  };

  /**
   * Helper function to recursively collect all child IDs
   * @param {Object} row - The row object containing children
   * @param {Set} updates - Set to collect all IDs
   */
  const collectChildrenRecursively = (row, updates) => {
    if (hasRowsWithChildren && row.children?.length) {
      row.children.forEach(child => {
        updates.add(child.id);
        // Recursively collect children of children
        if (dataMap.data[child.id]) {
          collectChildrenRecursively(dataMap.data[child.id], updates);
        }
      });
    }
  };
  
  /**
   * Selects All items
   */
  const selectAll = () => {
    const updates = new Set();
    const keysToProcess = searchValue ? dataMap.sortedIds : dataMap.filteredIds;

    // First collect all IDs including children
    keysToProcess.forEach((key) => {
      updates.add(key);
      if (dataMap.data[key]) {
        collectChildrenRecursively(dataMap.data[key], updates);
      }
    });

    // Batch update all collected IDs
    updates.forEach(key => {
      dataMap.data[key].checked = true;
      dataMap.selectedIds[key] = 1;
    });

    // Copy data from our dataMap into React state
    setData(generateDataSlice(dataMap, startIndex));
    setSelectedIds(dataMap.selectedIds);
  };

  /**
   * Deselect all items including children
   */
  const deselectAll = () => {
    const updates = new Set();
    const keys = Object.keys(dataMap.data);

    keys.forEach((key) => {
      updates.add(key);
      if (dataMap.data[key]) {
        collectChildrenRecursively(dataMap.data[key], updates);
      }
    });

    // Batch update all collected IDs
    updates.forEach(key => {
      dataMap.data[key].checked = false;
    });

    // Set selectedIds as empty
    dataMap.selectedIds = {};

    setData(generateDataSlice(dataMap, startIndex));
    setSelectedIds(dataMap.selectedIds);
  };

  /**
   * Handle shift-click row selection
   * @param {Object} row - Row object
   */
  const handleShiftClick = (row) => {
    const lastClickedRowId = lastClickedRow.original.id;
    const clickedRowId = row.original.id;
    const sortedIds = dataMap.sortedIds;
    
    // Find indices of the clicked rows
    const lastClickedRowIndex = sortedIds.findIndex((id) => id === lastClickedRowId);
    const clickedRowIndex = sortedIds.findIndex((id) => id === clickedRowId);
    
    if (lastClickedRowIndex === -1 || clickedRowIndex === -1) {
      return; // Invalid selection range
    }

    // Determine the selection range
    const start = Math.min(lastClickedRowIndex, clickedRowIndex);
    const end = Math.max(lastClickedRowIndex, clickedRowIndex);
    
    // Determine the target selection state based on the clicked row
    // If the clicked row is selected, we'll select everything in between
    // If the clicked row is not selected, we'll deselect everything in between
    const targetState = !dataMap.data[clickedRowId].checked;
    
    // Process all rows in the range at once
    const rowsToProcess = sortedIds.slice(start, end + 1);
    const updates = new Set(); // Track all IDs that need updating (including children)
    
    // First pass: collect all rows and their children that need updating
    rowsToProcess.forEach(id => {
      collectChildrenRecursively(dataMap.data[id], updates);
      updates.add(id);
      if (hasRowsWithChildren && dataMap.data[id].children?.length) {
        dataMap.data[id].children.forEach(child => {
          updates.add(child.id);
        });
      }
    });
    
    // Second pass: batch update all affected rows
    updates.forEach(id => {
      if (targetState) {
        dataMap.data[id].checked = true;
        dataMap.selectedIds[id] = 1;
      } else {
        dataMap.data[id].checked = false;
        delete dataMap.selectedIds[id];
      }
    });
  };

  // Add helper function to deselect parents
  const deselectParentRecursively = (row) => {
    if (!row.original.parentId || row.original.parentId === '00000000-0000-0000-0000-000000000000') return;
    
    const parentId = row.original.parentId;
    const parent = dataMap.data[parentId];
    if (parent && dataMap.selectedIds[parentId]) {
      dataMap.data[parentId].checked = false;
      delete dataMap.selectedIds[parentId];
      // Continue up the chain
      deselectParentRecursively({ original: parent });
    }
  };

  // Add helper function to check if all children are selected
  const areAllChildrenSelected = (parentId) => {
    const parent = dataMap.data[parentId];
    if (!parent || !parent.children || parent.children.length === 0) return false;
    
    return parent.children.every(child => dataMap.selectedIds[child.id]);
  };

  // Add helper function to select parents when all children are selected
  const selectParentIfAllChildrenSelected = (row) => {
    if (!row.original.parentId || row.original.parentId === '00000000-0000-0000-0000-000000000000') return;
    
    const parentId = row.original.parentId;
    const parent = dataMap.data[parentId];
    
    if (parent && areAllChildrenSelected(parentId)) {
      dataMap.data[parentId].checked = true;
      dataMap.selectedIds[parentId] = 1;
      // Continue up the chain
      selectParentIfAllChildrenSelected({ original: parent });
    }
  };

  /**
   * Selects the row
   * @param {Event} e - Event object
   * @param {Object} row - Row object
   * @param {boolean} value - Current checked state
   */
  const selectRow = (e, row, value) => {
    if (!row || !row.original) return; // Guard against invalid rows
    
    if (lastClickedRow && e.shiftKey && !singleSelect) {
      handleShiftClick(row);
    } else {
      const targetState = !value; // The state we want to set
      
      if (singleSelect) {
        // Clear all selections first
        Object.keys(dataMap.selectedIds).forEach(id => {
          dataMap.data[id].checked = false;
          delete dataMap.selectedIds[id];
        });
        
        // Select only the clicked row
        dataMap.data[row.original.id].checked = true;
        dataMap.selectedIds[row.original.id] = 1;
      } else {
        // Build a set of all IDs to update (including children)
        const updates = new Set([row.original.id]);
        collectChildrenRecursively(dataMap.data[row.original.id], updates);
        
        if (hasRowsWithChildren && row.original.children?.length) {
          row.original.children.forEach(child => {
            updates.add(child.id);
          });
        }
        
        // Batch update all affected rows
        updates.forEach(id => {
          if (targetState) {
            dataMap.data[id].checked = true;
            dataMap.selectedIds[id] = 1;
          } else {
            dataMap.data[id].checked = false;
            delete dataMap.selectedIds[id];
          }
        });

        // If we're deselecting a row, deselect all its parents
        if (!targetState) {
          deselectParentRecursively(row);
        } else {
          // If we're selecting a row, check if this causes any parent to have all children selected
          selectParentIfAllChildrenSelected(row);
        }
      }
    }
    
    setLastClickedRow(row);
    setData(generateDataSlice(dataMap, startIndex));
    setSelectedIds(dataMap.selectedIds);
  };

  /**
   * Handle clicking on expander button
   * @param {*} e 
   * @param {*} row 
   */
  const handleExpanderClick= (e, row) => {
    e.preventDefault();
    if (row.isExpanded) {
      let newList = expandeds.filter((item) => item !== row.original.id);
      setExpandeds(newList);
    } else {
      setExpandeds([...expandeds, row.original.id]);
    }
  }

  /**
   * Resizes the table
   * @param {*} event 
   */
  const handleMouseDown = (event) => {
    event.preventDefault();
    const startHeight = tableWrapperRef.current.getBoundingClientRect().height;
    const startY = event.pageY;

    // Set the maximum height to the window height minus 280px
    const maxHeight = window.innerHeight - 280;
  
    const handleMouseMove = (moveEvent) => {
      let newHeight = startHeight + (moveEvent.pageY - startY);
      newHeight = Math.min(newHeight, maxHeight);
      setContainerHeight(`${newHeight}px`);
    };
  
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  /**
   * Gets the class name for the cell (checkbox or expander)
   * @param {*} columnId 
   * @returns 
   */
  const getCellClassName = (columnId) => {
    switch(columnId) {
      case 'checkbox': return 'checkboxCell';
      case 'expander': return 'expanderCell'; 
      case 'actions': return 'actionsCell'; 
      default: return undefined; 
    }
  };

  /**
   * Renders a row in the table
   * @param {*} row - the row to render
   * @param {*} cellRenderer - the cell renderer to use 
   * @returns Clickable row if the modalId prop is passed, otherwise a normal row
   */
  const renderRow = ({ row, cellRenderer }) => {
    prepareRow(row);
    const isSelected = dataMap.selectedIds[row.original.id]; // Check if the row is selected
    const rowProps = row.getRowProps({ 
      style: { maxHeight: ROW_HEIGHT },
      className: `${row.original.parentId && row.original.parentId !== '00000000-0000-0000-0000-000000000000' ? 'expanded-row' : ''} ${isSelected ? 'selected-row' : ''}` // Add selected-row class if selected
    });
    const { key: rowKey, ...restRowProps } = rowProps;
  
    return (
      <tr key={rowKey} {...restRowProps}>
        {row.cells.map((cell) => {
          if (visibleColumns.includes(cell.column.id)) {
            const onClickHandler = (e) => {
              switch(cell.column.id) {
                case "checkbox":
                  return;
                case "expander":
                  return e.target.nodeName === 'SPAN' ? handleExpanderClick(e, row) : undefined;
                case "actions":
                  if (e.target.classList.contains('editAction')) {
                    return handleEditClick ? handleEditClick(e, row.original.id) : undefined;
                  }
                  if (e.target.classList.contains('deleteAction')) {
                    return handleSingleDelete ? handleSingleDelete(e, row.original.id) : undefined;
                  }
                  return;
                default:
                  return;
              }
            };
  
            const cellProps = cell.getCellProps({
              style: { height: ROW_HEIGHT }
            });
            const { key: cellKey, ...restCellProps } = cellProps;
  
            const cellClassName = getCellClassName(cell.column.id);
            return (
              <td
                key={cellKey}  // Pass key directly here
                className={`customBodyCell ${cellClassName}`}
                id="tableCell"
                onClick={onClickHandler}
                {...restCellProps}
              >
                {cell.column.id === 'icon' && 
                  <img className='customImgCell' src={cell.value} alt={cell.value} />
                }
                {cell.column.id !== 'icon' && 
                  <CellWithTooltip>
                    {cellRenderer(cell)}
                  </CellWithTooltip>
                }
              </td>
            );
          }
          return null;
        })}
      </tr>
    );
  };
  
  const renderHeader = (headerGroup) => {
    return headerGroup.headers.map((column) => {
      if (visibleColumns.includes(column.id)) {
        const headerClassName = getCellClassName(column.id);
        const isSpecialColumn = ['checkbox', 'expander', 'actions', 'viewReceivers'].includes(column.id);
        
        // Extract key from header props
        const { key, ...restHeaderProps } = column.getHeaderProps();

        return (
          <th
            key={key}
            {...restHeaderProps}
            className={`customHeaderCell ${headerClassName} d-flex align-items-center`}
            onClick={(e) => {
              if (!isSpecialColumn) {
                e.preventDefault();
                setSortBy(column.id);
              }
            }}
          >
            {singleSelect && column.id === 'checkbox' ? (
              <div style={{ width: '100%' }} />
            ) : (
              isSpecialColumn ? column.render('Header') : <FormattedMessage id={column.render('Header')} />
            )}
            
            {!isSpecialColumn && (
              <>
                <span>
                  {column.id === sortByCol ? (
                    !sortByAsc ? (
                      <img id="sortIconDown" className="sort-icon down" style={{ marginLeft: '2px' }} src={ArrowDownIcon} alt="Arrow Down" />
                    ) : (
                      <img id="sortIconUp" className="sort-icon up" style={{ marginLeft: '2px' }} src={ArrowDownIcon} alt="Arrow Down" />
                    )
                  ) : null}
                </span>
                <div
                  {...column.isResizing && tableModal.updateColumnWidths(pageType, column)}
                  {...column.getResizerProps()}
                  className={`resizer ${column.isResizing ? 'isResizing' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <img src={DragIcon} className="drag-icon" draggable="false" alt="Drag Icon" />
                </div>
              </>
            )}
          </th>
        );
      }
      return null;
    });
  };  
  
  const defaultColumn = React.useMemo(
    () => ({

    }),
    []
  )

	// Initializing useTable hooks
	const {
    getTableProps,
    headerGroups,
    prepareRow,
    rows,
  } = useTable(
    {
      columns: columnsNew,
      data,
      defaultColumn,
      initialState: { 
				hiddenColumns: columnsNew.filter((column) => !column.isVisible).map((column) => column.id),
			},
      selectRow,
      selectAll,
      deselectAll,
      dataCount,
      totalDataCount,
      selectedIds,
      sortByCol,
      setSortBy,
      startIndex,
      setSearchValue,
      visibleColumns,
      autoResetExpanded: false,
    },
    // useBlockLayout,
    useFlexLayout,
    useResizeColumns,
    useSortBy,
    useExpanded,
    useRowSelect,    
  )
  
  // Handle data status message
  useEffect(() => {
    // Check if data is loaded
    if (dataLoaded) {
      // Data is loaded, check for data count
      if (dataCount === 0) {
        setDataStatus('NO_DATA_FOUND');
      } else {
        // Data is loaded and data count is greater than 0
        setDataStatus('DATA_LOADED');
      }
    } else {
      // Data is still loading
      // console.log('DATA IS LOADING');
      setDataStatus('DATA_IS_LOADING');
    }
  }, [dataLoaded, dataCount]);

  return (
    <div className="tableWrapper">
      <div className="title d-flex justify-content-between align-items-center">
        <FormattedMessage id={pageType} />
        {titleButtons}
      </div>
      <div className="tableContainer">
        <div id="toolbar">
          {/* Selection Toolbar */}
          {selectionToolbar && (
            <div id="selection-toolbar" className="toolbar-top">
              {selectionToolbar}
            </div>
          )}
          {/* Toolbar */}
          <div id="toolbar-top" className="toolbar-top row">
            <div className="col d-flex justify-content-start">
              <div className="btn-group" role="group">
                {toolbarLeftButtons}
                <ColumnToggleButton columns={columns} visibleColumns={visibleColumns} toggleColumn={toggleColumn} />
              </div>
              <Searchbar search={setSearchValue} />
            </div>
            <div className="col d-flex justify-content-end">
              {toolbarRightButtons}
            </div>
          </div>
          {footerButtons &&
            <div id="toolbar-bottom" className="row">
              <div className="col d-flex justify-content-start">
                <div className="selected-count">
                  {Object.keys(selectedIds).length} <FormattedMessage id="SELECTED_TEXT" defaultMessage="items selected" />
                </div>
                {footerButtons}
              </div>
            </div>
          }
        </div>
        <div className="scrollBarWrapper" id="scrollBarWrapper" ref={tableWrapperRef} style={{ maxHeight: containerHeight }}>
          { /* Use safe values when startIndex is not provided */ }
          { /* eslint-disable-next-line no-unused-vars */ }
          <table className="table" id="mainTable" key={pageType} {...getTableProps()}>
            <thead>
              <tr>
                {headerGroups.map((headerGroup) => renderHeader(headerGroup))}
              </tr>
            </thead>
            <tbody>
              <tr id='top-block' style={{ borderWidth: 0, height: (startIndex || 0) * ROW_HEIGHT, overflow: 'hidden' }}>
                <td style={{ overflow: 'hidden', borderWidth: 0, padding: 0 }} />
              </tr>
              {data.length === 0 ? (
                <tr>
                  <td className="fullWidthCell" colSpan={Math.max(visibleColumns.length, 1)} style={{ borderWidth: 0, padding: 0 }}>
                    <div className="data-status text-center">
                      <h3 className="fw-bold">{dataStatus !== "" ? <FormattedMessage id={dataStatus} /> : ""}</h3>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  prepareRow(row);
                  let canExpanded = expandeds.includes(row.original.id);
                  // if isExpanded is defined, then we will use it to expand/collapse the row. Otherwise, we will use the expandedsRef value.
                  let canToggle = row.isExpanded === undefined || row.isExpanded === false ? canExpanded : !canExpanded
                  if  (canToggle)
                    row.toggleRowExpanded(canExpanded)
                  return (
                    <React.Fragment key={row.id}>
                      {renderRow({ row, cellRenderer: (cell) => cell.render('Cell') })}
                      {row.isExpanded && (
                        <tr><td colSpan={visibleColumns.length} style={{padding: 0}}>
                          <SubTable {...subTableProps} row={row}/>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
              <tr id='bottom-block' style={{ borderWidth: 0, height: (dataCount - data.length - (startIndex || 0)) * ROW_HEIGHT, overflow: 'hidden' }}>
                <td style={{ overflow: 'hidden', borderWidth: 0, padding: 0 }} />
              </tr>
            </tbody>
          </table>
        </div>
        <div className="resize-handle" onMouseDown={handleMouseDown}>
          <div></div> 
        </div>
      </div>
      <div className="tableFooter">
        <FormattedMessage id="CURRENTLY_VIEWING" values={{dataCount: dataCount}} />&nbsp;&nbsp;|&nbsp;&nbsp;
        <FormattedMessage id="TOTAL_COUNT" values={{totalDataCount: totalDataCount}} />&nbsp;&nbsp;|&nbsp;&nbsp;
        <FormattedMessage id="SELECTED" values={{selectedDataCount: Object.keys(selectedIds).length}} />
      </div>
      <Toast
        success={success}
        error={error}
        setSuccess={setSuccess}
        setError={setError}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(Table)